import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEventEmailTemplates } from "@/hooks/useEventEmailTemplates";
import { useReminderEmailDialogState } from "@/hooks/useReminderEmailDialogState";
import type {
  EventApiResponse,
  RecurrenceScope,
  Reminder,
  UserShort,
} from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";
import { dedupeReminderItems } from "@/lib/utils/event/form-initializer";
import { formatEventListRow } from "@/lib/utils/v2/event-date";
import { ReminderEmailPanel } from "./ReminderEmailPanel";

interface ReminderEmailDialogProps {
  open: boolean;
  event: EventApiResponse;
  participants: UserShort[];
  primaryImageGuid?: string | null;
  onOpenChange: (open: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  hasEmailUnsavedChanges: boolean;
  onShowUnsavedWarning: () => void;
  onRemindersUpdated?: (scope?: RecurrenceScope) => void;
  hasRecurrence?: boolean;
}

export const ReminderEmailDialog = ({
  open,
  event,
  participants,
  primaryImageGuid,
  onOpenChange,
  onDirtyChange,
  hasEmailUnsavedChanges,
  onShowUnsavedWarning,
  onRemindersUpdated,
  hasRecurrence = false,
}: ReminderEmailDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reminderEmailSubject, setReminderEmailSubject] = useState<string>("");
  const [reminderEmailBodyText, setReminderEmailBodyText] =
    useState<string>("");
  const [reminderEmailBodyHtml, setReminderEmailBodyHtml] = useState<
    string | null
  >(null);
  const { fetchReminderTemplate } = useEventEmailTemplates();

  const uniqueReminders = useMemo(
    () => dedupeReminderItems(event?.reminders ?? []),
    [event?.reminders],
  );
  const eventDatetimeLabel = useMemo(() => {
    if (!event?.start_datetime)
      return t("common.dateNotAvailable", "Date not available");
    const baseLabel = formatEventListRow(event, {
      year: "auto",
      displayTimezone: "event",
    });
    const tzLabel = event.time_zone_str?.trim();
    return tzLabel ? `${baseLabel} (${tzLabel})` : baseLabel;
  }, [event, t]);
  const {
    emailScope,
    setEmailScope,
    setSelectedReminderId,
    selectedReminder,
    initializeForOpen: initializeReminderDialog,
    reminderSelectValue,
    handleReminderSelect,
  } = useReminderEmailDialogState({ reminders: uniqueReminders as Reminder[] });

  useEffect(() => {
    if (!selectedReminder) {
      setReminderEmailSubject("");
      setReminderEmailBodyText("");
      setReminderEmailBodyHtml(null);
      return;
    }

    setReminderEmailSubject(selectedReminder.email_subject ?? "");
    setReminderEmailBodyText(selectedReminder.email_body_text ?? "");
    setReminderEmailBodyHtml(selectedReminder.email_body_html ?? null);
  }, [selectedReminder]);

  useEffect(() => {
    if (
      !open ||
      emailScope !== "reminder" ||
      !selectedReminder?.id ||
      hasEmailUnsavedChanges
    )
      return;

    const controller = new AbortController();
    const loadTemplate = async () => {
      try {
        if (!selectedReminder.id) {
          throw new Error("Reminder ID is required");
        }
        const template = await fetchReminderTemplate(
          event.id,
          selectedReminder.id,
          {
            signal: controller.signal,
          },
        );
        if (controller.signal.aborted || hasEmailUnsavedChanges) return;
        setReminderEmailSubject(template.subject ?? "");
        setReminderEmailBodyText(template.body_text ?? "");
        setReminderEmailBodyHtml(template.body_html ?? null);
      } catch (error) {
        const errorName = (error as { name?: string } | null)?.name;
        if (errorName !== "CanceledError" && errorName !== "AbortError") {
          console.error("Failed to fetch reminder email template:", error);
        }
      }
    };

    loadTemplate();

    return () => controller.abort();
  }, [
    emailScope,
    event.id,
    fetchReminderTemplate,
    hasEmailUnsavedChanges,
    open,
    selectedReminder?.id,
  ]);

  const handleRemindersUpdated = (scope?: RecurrenceScope) => {
    queryInvalidationHelpers.invalidateEventQueries(queryClient);
    queryClient.invalidateQueries({ queryKey: ["eventDetail", event.id] });
    onRemindersUpdated?.(scope);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          initializeReminderDialog();
          onOpenChange(true);
        } else if (hasEmailUnsavedChanges) {
          onShowUnsavedWarning();
        } else {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {t("eventManage.reminderAndEmailSettings")}
          </DialogTitle>
        </DialogHeader>
        <ReminderEmailPanel
          eventId={event.id}
          eventTitle={event.title}
          eventDescription={event.description}
          eventDatetimeLabel={eventDatetimeLabel}
          eventUrl={event.online_location}
          eventData={event}
          scope={emailScope}
          defaultSubject={reminderEmailSubject}
          defaultBodyText={reminderEmailBodyText}
          defaultBodyHtml={reminderEmailBodyHtml}
          reminderId={
            emailScope === "entry" ? null : (selectedReminder?.id ?? null)
          }
          primaryImageGuid={primaryImageGuid}
          reminders={uniqueReminders}
          participants={participants}
          onDirtyChange={onDirtyChange}
          isOpen={open}
          onReminderChange={setSelectedReminderId}
          onScopeChange={setEmailScope}
          reminderSelectValue={reminderSelectValue}
          onReminderSelect={handleReminderSelect}
          showImageButton={(event?.content?.files ?? []).length > 0}
          onRemindersUpdated={handleRemindersUpdated}
          hasRecurrence={hasRecurrence}
        />
      </DialogContent>
    </Dialog>
  );
};
